import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Autocomplete,
  Grid
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useApi } from '../../contexts/ApiContext';
import { usePermissions } from '../../hooks/usePermissions';

const AgentDailyReport = () => {
  const api = useApi();
  const { canAction } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agentsData, setAgentsData] = useState([]);
  const getLocalDateString = (date = new Date()) => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getLocalDateString());

  // Filtros
  const [supervisors, setSupervisors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Ordenamiento
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    loadFiltersData();
    // No cargar el reporte automáticamente, esperar a que el usuario haga click en Actualizar
  }, []);

  const loadFiltersData = async () => {
    try {
      const [agentsRes, skillsRes, campaignsRes] = await Promise.all([
        api.agents.getAll().catch(err => {
          console.error('Error loading agents:', err);
          return { success: false, data: [] };
        }),
        api.skills.getAll().catch(err => {
          console.error('Error loading skills:', err);
          return { success: false, data: [] };
        }),
        api.campaigns.getAll().catch(err => {
          console.error('Error loading campaigns:', err);
          return { success: false, data: [] };
        })
      ]);

      console.log('[AgentDailyReport] Loaded filters:', { agentsRes, skillsRes, campaignsRes });

      if (agentsRes && agentsRes.success) {
        const agentsPayload = agentsRes.data?.agents || agentsRes.data || [];
        const agents = Array.isArray(agentsPayload) ? agentsPayload : [];
        console.log('[AgentDailyReport] Agents loaded:', agents.length, 'agents');
        console.log('[AgentDailyReport] First agent sample:', agents[0]);

        const nonVirtualAgents = agents.filter(a => !a.virtual);

        // Lookup para etiquetas amigables
        const agentLookup = new Map();
        agents.forEach(a => {
          if (a.agent) {
            agentLookup.set(a.agent, a.name || a.agent);
          }
        });

        // Extraer supervisores y gerentes únicos (usando supervisor_id y manager_id)
        const uniqueSupervisorIds = [...new Set(agents.map(a => a.supervisor_id).filter(Boolean))];
        const uniqueManagerIds = [...new Set(agents.map(a => a.manager_id).filter(Boolean))];

        const supervisorsList = uniqueSupervisorIds.map(id => ({
          label: agentLookup.get(id) || id,
          value: id
        }));

        const managersList = uniqueManagerIds.map(id => ({
          label: agentLookup.get(id) || id,
          value: id
        }));

        setAllAgents(nonVirtualAgents);
        setSupervisors(supervisorsList);
        setManagers(managersList);
      }

      if (skillsRes && skillsRes.success) {
        // La respuesta viene como { success: true, data: { skills: [...] } }
        const skillsData = skillsRes.data?.skills || skillsRes.data || [];
        console.log('[AgentDailyReport] Skills loaded:', skillsData.length, 'skills');
        console.log('[AgentDailyReport] First skill sample:', skillsData[0]);
        setSkills(Array.isArray(skillsData) ? skillsData : []);
      }

      if (campaignsRes && campaignsRes.success) {
        // La respuesta viene como { success: true, data: { campaigns: [...] } }
        const campaignsData = campaignsRes.data?.campaigns || [];
        console.log('[AgentDailyReport] Campaigns loaded:', campaignsData.length, 'campaigns');
        console.log('[AgentDailyReport] First campaign sample:', campaignsData[0]);
        setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
      }
    } catch (err) {
      console.error('Error loading filters:', err);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { date: selectedDate };

      // Agregar filtros a params
      if (selectedSupervisor) params.supervisor = selectedSupervisor.value;
      if (selectedManager) params.manager = selectedManager.value;
      if (selectedSkill) params.skill = selectedSkill.id; // skill usa 'id', no 'id_skill'
      if (selectedCampaign) params.campaign = selectedCampaign.idcampaign;
      if (selectedAgents.length > 0) {
        params.agents = selectedAgents.map(a => a.agent).join(',');
      }

      console.log('[AgentDailyReport] Fetching with params:', params);

      const response = await api.agents.getDailySummary(params);
      console.log('[AgentDailyReport] Response:', response);

      if (response.success) {
        setAgentsData(response.data.agents || []);
      } else {
        setError(response.message || 'Error al cargar el reporte');
      }
    } catch (err) {
      setError('Error al cargar el reporte de agentes: ' + (err.message || err));
      console.error('Error fetching agents report:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDuration = (statusArray, status) => {
    const found = statusArray.find(s => s.status === status);
    return found ? found.total_duration : 0;
  };

  const calculateWorkTime = (statusArray) => {
    const available = getStatusDuration(statusArray, 'AVAILABLE');
    const preview = getStatusDuration(statusArray, 'PRECALL');
    const inCall = getStatusDuration(statusArray, 'INCALL');
    const acw = getStatusDuration(statusArray, 'ACW');
    return available + preview + inCall + acw;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'name':
          aValue = a.agent.name;
          bValue = b.agent.name;
          break;
        case 'document':
          aValue = a.agent.document_number || '';
          bValue = b.agent.document_number || '';
          break;
        case 'calls':
          aValue = a.callStats.total_calls;
          bValue = b.callStats.total_calls;
          break;
        case 'connection_time':
          aValue = a.statusSummary.total_connection_time;
          bValue = b.statusSummary.total_connection_time;
          break;
        case 'available':
          aValue = getStatusDuration(a.statusSummary.by_status, 'AVAILABLE');
          bValue = getStatusDuration(b.statusSummary.by_status, 'AVAILABLE');
          break;
        case 'work_time':
          aValue = calculateWorkTime(a.statusSummary.by_status);
          bValue = calculateWorkTime(b.statusSummary.by_status);
          break;
        default:
          aValue = a.agent.name;
          bValue = b.agent.name;
      }

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };

  const exportToCSV = () => {
    if (!agentsData.length) return;

    const headers = [
      'Agente',
      'Documento',
      'Llamadas Atendidas',
      'Colgadas por Agente',
      'Colgadas por Usuario',
      'Tiempo en Llamada Total',
      'Promedio en Llamada',
      'Promedio ACW',
      'Promedio Cola',
      'Tiempo Disponible',
      'Tiempo Preview',
      'Tiempo En Llamada',
      'Tiempo ACW',
      'Tiempo Pausa',
      'Tiempo Break',
      'Tiempo Baño',
      'Tiempo Almuerzo',
      'Tiempo Sistema',
      'Tiempo de Trabajo',
      'Tiempo Conectado',
      'Primera Conexión',
      'Última Desconexión',
      'Desconexiones'
    ];

    const rows = sortData(agentsData).map(agent => {
      const byStatus = agent.statusSummary.by_status;
      const workTime = calculateWorkTime(byStatus);

      return [
        agent.agent.name,
        agent.agent.document_number || '-',
        agent.callStats.total_calls,
        agent.callStats.hangup_by_agent,
        agent.callStats.hangup_by_user,
        formatDuration(agent.callStats.total_call_duration),
        formatDuration(agent.callStats.avg_call_duration),
        formatDuration(agent.callStats.avg_acw_duration),
        formatDuration(agent.callStats.avg_queue_duration),
        formatDuration(getStatusDuration(byStatus, 'AVAILABLE')),
        formatDuration(getStatusDuration(byStatus, 'PRECALL')),
        formatDuration(getStatusDuration(byStatus, 'INCALL')),
        formatDuration(getStatusDuration(byStatus, 'ACW')),
        formatDuration(getStatusDuration(byStatus, 'PAUSE')),
        formatDuration(getStatusDuration(byStatus, 'BREAK')),
        formatDuration(getStatusDuration(byStatus, 'BATHROOM')),
        formatDuration(getStatusDuration(byStatus, 'LUNCH')),
        formatDuration(getStatusDuration(byStatus, 'NOTREADY')),
        formatDuration(workTime),
        formatDuration(agent.statusSummary.total_connection_time),
        formatTime(agent.connectionStats.first_connection),
        formatTime(agent.connectionStats.last_disconnection),
        agent.connectionStats.disconnection_count
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_agentes_${selectedDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortedData = sortData(agentsData);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              type="date"
              label="Fecha"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={supervisors}
              getOptionLabel={(option) => option.label || ''}
              value={selectedSupervisor}
              onChange={(e, newValue) => setSelectedSupervisor(newValue)}
              renderInput={(params) => <TextField {...params} label="Supervisor" size="small" />}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={managers}
              getOptionLabel={(option) => option.label || ''}
              value={selectedManager}
              onChange={(e, newValue) => setSelectedManager(newValue)}
              renderInput={(params) => <TextField {...params} label="Gerente" size="small" />}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={skills}
              getOptionLabel={(option) => option.skillname || ''}
              value={selectedSkill}
              onChange={(e, newValue) => setSelectedSkill(newValue)}
              renderInput={(params) => <TextField {...params} label="Skill" size="small" />}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={campaigns}
              getOptionLabel={(option) => option.descripcion || ''}
              value={selectedCampaign}
              onChange={(e, newValue) => setSelectedCampaign(newValue)}
              renderInput={(params) => <TextField {...params} label="Campaña" size="small" />}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              options={allAgents}
              getOptionLabel={(option) => `${option.agent} - ${option.name || ''}`}
              value={selectedAgents}
              onChange={(e, newValue) => setSelectedAgents(newValue || [])}
              renderInput={(params) => (
                <TextField {...params} label="Agentes" size="small" placeholder="Buscar..." />
              )}
              size="small"
              filterSelectedOptions
              isOptionEqualToValue={(opt, val) => opt?.agent === val?.agent}
            />
          </Grid>
        </Grid>

        <Box display="flex" gap={2} mt={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="contained"
            onClick={fetchReport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            Actualizar
          </Button>
          {canAction('export_reports') && (
            <Button
              variant="outlined"
              onClick={exportToCSV}
              disabled={loading || !agentsData.length}
              startIcon={<DownloadIcon />}
            >
              Exportar CSV
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            Total agentes: {agentsData.length}
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !agentsData.length && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {!loading && agentsData.length === 0 && !error && (
        <Alert severity="info">
          No hay datos disponibles para la fecha y filtros seleccionados
        </Alert>
      )}

      {agentsData.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            position: 'relative',
            width: 0,
            minWidth: '100%',
            overflowX: 'auto',
            overflowY: 'auto'
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Agente
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  <TableSortLabel
                    active={orderBy === 'document'}
                    direction={orderBy === 'document' ? order : 'asc'}
                    onClick={() => handleRequestSort('document')}
                  >
                    Documento
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  <TableSortLabel
                    active={orderBy === 'calls'}
                    direction={orderBy === 'calls' ? order : 'asc'}
                    onClick={() => handleRequestSort('calls')}
                  >
                    Llamadas
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Colg. Agente
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Colg. Usuario
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Llamada Total
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Prom. Llamada
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Prom. ACW
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  <TableSortLabel
                    active={orderBy === 'available'}
                    direction={orderBy === 'available' ? order : 'asc'}
                    onClick={() => handleRequestSort('available')}
                  >
                    T. Disponible
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Preview
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. En Llamada
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. ACW
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Pausa
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Break
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Baño
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Almuerzo
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  T. Sistema
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  <TableSortLabel
                    active={orderBy === 'work_time'}
                    direction={orderBy === 'work_time' ? order : 'asc'}
                    onClick={() => handleRequestSort('work_time')}
                  >
                    T. Trabajo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  <TableSortLabel
                    active={orderBy === 'connection_time'}
                    direction={orderBy === 'connection_time' ? order : 'asc'}
                    onClick={() => handleRequestSort('connection_time')}
                  >
                    T. Conectado
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Primera Con.
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Última Desc.
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                  Desconex.
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((agent) => {
                const byStatus = agent.statusSummary.by_status;
                const workTime = calculateWorkTime(byStatus);

                return (
                  <TableRow key={agent.agent.agent} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {agent.agent.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {agent.agent.document_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {agent.callStats.total_calls}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {agent.callStats.hangup_by_agent}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {agent.callStats.hangup_by_user}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(agent.callStats.total_call_duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(agent.callStats.avg_call_duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(agent.callStats.avg_acw_duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'AVAILABLE'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'PRECALL'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'INCALL'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'ACW'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'PAUSE'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'BREAK'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'BATHROOM'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'LUNCH'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatDuration(getStatusDuration(byStatus, 'NOTREADY'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {formatDuration(workTime)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatDuration(agent.statusSummary.total_connection_time)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatTime(agent.connectionStats.first_connection)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatTime(agent.connectionStats.last_disconnection)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {agent.connectionStats.disconnection_count}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AgentDailyReport;
